import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BenchmarkType, DOMAIN_FACT_TYPES, R1FactType } from '@/api/rest/resources/types/fact';
import { WidgetSlot, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
import { useBenchmarkForFact, useBenchmarkIndicatorsForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting/formatKgToTon';
import { getStyleForBenchmark } from '@/utils/getStyleForBenchmark';

import { kgToTonneFormatter } from '../Charts/utils';

export const useNatureHealthConfiguration = () => {
  const { t } = useTranslation();
  const membershipType = useMembershipType();
  const pathSet = usePathSet(membershipType);
  const navigate = useNavigate();

  const healthFacts = useOrganizationIndicatorReportingFact([
    DOMAIN_FACT_TYPES.carbon_combined_per_ha,
    DOMAIN_FACT_TYPES.water_combined_per_ha,
    DOMAIN_FACT_TYPES.biodiversity_combined_per_ha,
    DOMAIN_FACT_TYPES.soil_combined_per_ha,
    DOMAIN_FACT_TYPES.social_combined_per_ha,
    R1FactType.social_life_expectancy_max,
    R1FactType.social_life_expectancy_min,
  ]);
  // const carbonLatest = formatFactKilogramsToTonnes(healthFacts[`${DOMAIN_FACT_TYPES.carbon_combined_per_ha}`]);
  const waterLatest = healthFacts[`${DOMAIN_FACT_TYPES.water_combined_per_ha}`];
  const biodiversityLatest = healthFacts[`${DOMAIN_FACT_TYPES.biodiversity_combined_per_ha}`];
  const soilLatest = formatFactKilogramsToTonnes(healthFacts[`${DOMAIN_FACT_TYPES.soil_combined_per_ha}`]);
  const socialLatest = healthFacts[`${DOMAIN_FACT_TYPES.social_combined_per_ha}`];

  // const carbonBenchmarks = useBenchmarkIndicatorsForOrganization(
  //   DOMAIN_FACT_TYPES.carbon_combined_per_ha_benchmark,
  //   null,
  //   kgToTonneFormatter,
  // );
  const soilBenchmarks = useBenchmarkIndicatorsForOrganization(
    DOMAIN_FACT_TYPES.soil_combined_benchmark_per_ha,
    null,
    kgToTonneFormatter,
  );
  const waterBenchmarks = useBenchmarkIndicatorsForOrganization(DOMAIN_FACT_TYPES.water_combined_benchmark_per_ha);
  const biodiversityBenchmarks = useBenchmarkIndicatorsForOrganization(
    DOMAIN_FACT_TYPES.biodiversity_combined_benchmark_per_ha,
  );
  const socialHigh = healthFacts[`${R1FactType.social_life_expectancy_max}`]?.value;
  const socialLow = healthFacts[`${R1FactType.social_life_expectancy_min}`]?.value;
  const socialModerate =
    !isNullish(socialHigh) && !isNullish(socialLow) ? ((socialHigh as number) + (socialLow as number)) / 2 : null;
  const socialBenchmarks = [socialLow, socialModerate, socialHigh];

  // const carbonBenchmarkType = useBenchmarkForFact(carbonBenchmarks, carbonLatest?.value);
  const waterBenchmarkType = useBenchmarkForFact(waterBenchmarks, waterLatest?.value);
  const biodiversityBenchmarkType = useBenchmarkForFact(biodiversityBenchmarks, biodiversityLatest?.value);
  const soilBenchmarkType = useBenchmarkForFact(soilBenchmarks, soilLatest?.value);
  const socialBenchmarkType = useBenchmarkForFact(socialBenchmarks, socialLatest?.value);

  const DOMAIN_OPTIONS = [
    {
      domain: DOMAIN_FACT_TYPES.carbon_combined_per_ha,
      // benchmarkType: carbonBenchmarkType,
      to: pathSet.carbonDetails,
      // style: carbonBenchmarkType ? getStyleForBenchmark(carbonBenchmarkType) : null,
    },
    {
      domain: DOMAIN_FACT_TYPES.water_combined_per_ha,
      benchmarkType: waterBenchmarkType,
      to: pathSet.waterDetails,
      style: waterBenchmarkType ? getStyleForBenchmark(waterBenchmarkType) : null,
    },
    {
      domain: DOMAIN_FACT_TYPES.biodiversity_combined_per_ha,
      benchmarkType: biodiversityBenchmarkType,
      to: pathSet.biodiversityDetails,
      style: biodiversityBenchmarkType ? getStyleForBenchmark(biodiversityBenchmarkType) : null,
    },
    {
      domain: DOMAIN_FACT_TYPES.soil_combined_per_ha,
      benchmarkType: soilBenchmarkType,
      to: pathSet.soilDetails,
      style: soilBenchmarkType ? getStyleForBenchmark(soilBenchmarkType) : null,
    },
    {
      domain: DOMAIN_FACT_TYPES.social_combined_per_ha,
      benchmarkType: socialBenchmarkType,
      to: pathSet.socialDetails,
      style: socialBenchmarkType ? getStyleForBenchmark(socialBenchmarkType) : null,
    },
  ];
  return {
    name: 'default_ecosystem_health_overview',
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      header: t('ecosystemHealth.header'),
      headerTooltip: t('ecosystemHealth.tooltip.body'),
      styles: {
        root: 'border-none w-full',
        header: 'border-none typography-h3 text-text-primary px-0',
        body: 'border border-neutral-black-12 rounded-2xl flex flex-row flex-wrap gap-4 p-2 h-full justify-between',
      },
    },
    widgets: DOMAIN_OPTIONS.map(
      (opt): WidgetSlot => ({
        name: `domain_row_${opt.domain}`,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className:
            'p-2 justify-center text-text-primary hover:cursor-pointer hover:bg-neutral-black-4 hover:p-2 hover:rounded-2xl',
          onClick: () => navigate(opt.to),
        },
        widgets: [
          {
            name: `domain_${opt.domain}`,
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'flex-col gap-2 md:flex-row items-center justify-between',
            },
            widgets: [
              {
                name: `domain_name_${opt.domain}`,
                type: WidgetType.a_text,
                visible: true,
                relatedData: {
                  text: t(`ecosystemHealth.domain.${opt.domain as keyof typeof DOMAIN_FACT_TYPES}.title`),
                  className: 'typography-h4',
                },
              },
              {
                name: `domain_benchmark_${opt.domain}`,
                type: WidgetType.m_icon_button,
                visible: !isNullish(opt.benchmarkType),
                relatedData: {
                  text: !isNullish(opt.benchmarkType)
                    ? t(
                        `ecosystemHealth.benchmark.${opt.benchmarkType as BenchmarkType.high | BenchmarkType.moderate | BenchmarkType.low}`,
                      )
                    : '',
                  className: `typography-body2 font-semibold w-fit text-error-dark px-4 py-2 capitalize items-center`,
                  style: {
                    color: opt.style?.text,
                    backgroundColor: opt.style?.primary,
                  },
                },
              },
            ],
          },
        ],
      }),
    ),
  } as WidgetSlotConfiguration;
};
